import { Link } from "react-router-dom";
import { MapPin, Building2, Play } from "lucide-react";
import { formatPrice, whatsappLink } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";
import FavoriteButton from "@/components/FavoriteButton";

export default function ProjectCard({ project }) {
  const {
    id, name, builder_name, location, project_type, price_from, price_to,
    bhk_options = [], hero_image, verified, possession_date, video_url, rera_number,
  } = project;

  return (
    <article
      data-testid={`project-card-${id}`}
      className="group bg-white border border-border rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(6,78,59,0.08)]"
    >
      <Link to={`/projects/${id}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={hero_image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />
          {verified && (
            <div className="absolute top-3 left-3">
              <VerifiedBadge small />
            </div>
          )}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/95 text-forest font-medium">
              {project_type}
            </span>
            <FavoriteButton projectId={id} />
          </div>
          {video_url && (
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full">
              <Play className="w-3 h-3 fill-white" /> 60-sec Tour
            </div>
          )}
        </div>
      </Link>

      <div className="p-5">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <MapPin className="w-3.5 h-3.5" /> {location}, Hyderabad
        </div>
        <Link to={`/projects/${id}`}>
          <h3 className="font-heading font-bold text-lg text-forest leading-tight tracking-tight hover:underline decoration-gold decoration-2 underline-offset-4">
            {name}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
          <Building2 className="w-3.5 h-3.5" /> by {builder_name}
        </div>
        <div className="mt-2">
          {rera_number ? (
            <span data-testid={`rera-card-registered-${id}`} className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-forest/5 text-forest border border-forest/10">
              RERA Registered · {rera_number}
            </span>
          ) : (
            <span data-testid={`rera-card-pending-${id}`} className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-cream text-muted-foreground border border-border">
              RERA: Not yet registered
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {bhk_options.slice(0, 4).map((b) => (
            <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-cream border border-border text-forest">
              {b}
            </span>
          ))}
        </div>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Starting</div>
            <div className="font-heading text-2xl font-bold text-forest leading-none mt-0.5">
              {formatPrice(price_from)}
              {price_to && price_to !== price_from && (
                <span className="text-sm text-muted-foreground font-sans font-normal ml-1">– {formatPrice(price_to)}</span>
              )}
            </div>
            {possession_date && (
              <div className="text-[11px] text-muted-foreground mt-1">Possession · {possession_date}</div>
            )}
          </div>
          <a
            href={whatsappLink(`Hi, I'm interested in ${name} at ${location}.`)}
            target="_blank" rel="noreferrer"
            data-testid={`project-card-whatsapp-${id}`}
            className="text-xs font-medium rounded-full bg-whatsapp/10 text-whatsapp hover:bg-whatsapp hover:text-white px-3 py-1.5 transition-all"
          >
            WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
}
